import { Box, Card, CardContent, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { createScopedLogger } from "../../../common";
import IconComponent from "../../../common/components/IconComponent";
import Spinner from "../../../common/components/Spinner";
import { useErrorHandler } from "../../../common/hooks";
import BundleContext from "../../Contexts/BundleContext";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import { ValueCounterModel } from "../../Models/Dashboards/ValueCounterModel";
import formatPropertyValue from "../DataDisplay/formatPropertyValue";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";

const logger = createScopedLogger("ValueCounterComponent");

const ValueCounterComponent: React.FC<IComponentProps<ValueCounterModel>> = ({ component, context, componentPath = [] }) => {
  const errorHandler = useErrorHandler();
  const bundleContext = useContext(BundleContext);

  const [data, setData] = useState<any>(null);
  const [dataPending, setDataPending] = useState<boolean>(false);

  const [dataRefreshMarker, setDataRefreshMarker] = useState<number>(0);

  const selectionProps = useComponentSelection({
    component,
    componentPath,
    isContainer: false,
  });

  const handleLoadData = async () => {
    if (!component.GetData) {
      logger.warn("GetData expression is not defined.");
      return;
    }

    setDataPending(true);
    try {
      const result = await context.Evaluator.evaluate(component.GetData, context);
      setData(result);
    } catch (error) {
      errorHandler(error);
    } finally {
      setDataPending(false);
    }
  };

  useEffect(() => {
    handleLoadData();
  }, [component.GetData]);

  const value = data ? formatPropertyValue(data, component.Property, undefined, undefined, bundleContext.Settings?.PhoneNumberFormat, bundleContext.Settings?.CurrencyCode ?? "USD") : "-";

  const componentLayoutSx = getComponentLayout(component);

  return (
    <Card sx={componentLayoutSx} {...selectionProps}>
      <CardContent>
        {dataPending ? (
          <Spinner />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {component.Property.Label}
              </Typography>
              <Typography variant="h4" component="div">
                {value}
              </Typography>
            </Box>
            {component.Property.IconName && (
              <Box sx={{ color: component.Property.Color || "primary.main", fontSize: 40 }}>
                <IconComponent iconName={component.Property.IconName} />
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ValueCounterComponent;
